import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import SchoolLogo from "@/components/SchoolLogo";
import { toast } from "sonner";
import {
  Printer, Download, ImageDown, Copy, ExternalLink, Award, ShieldCheck,
  GraduationCap, TrendingUp, Activity, PlayCircle, Instagram, Twitter,
  Youtube, Music2, Globe, Video, Link2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Portfolio() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/players/${slug}/portfolio`)
      .then(({ data }) => {
        setD(data);
        const p = data.player;
        document.title = `${p.first_name} ${p.last_name} | Recruiting Portfolio | CBR Prospects`;
      })
      .catch(() => setErr(true));
    return () => { document.title = "CBR Prospects"; };
  }, [slug]);

  if (err) return <Centered>Portfolio not available.</Centered>;
  if (!d) return <Centered>Loading portfolio…</Centered>;

  const p = d.player;
  const rk = d.rankings || {};
  const dev = d.development || {};
  const pdfUrl = `${BACKEND}/api/players/${p.id}/portfolio.pdf`;
  const cardUrl = `${BACKEND}/api/players/${p.id}/portfolio-card.png`;
  const shareUrl = d.portfolio_url;

  const copy = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Portfolio link copied"); track("copy_portfolio_link", p.id, "portfolio"); };

  return (
    <div className="min-h-screen bg-[#F1F3F6]" data-testid="portfolio-page">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-20 bg-[#0A192F] text-white border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
          <span className="font-display uppercase tracking-tight text-sm mr-auto flex items-center gap-2">
            <span className="bg-[#E63946] px-1.5 py-0.5 text-xs">CBR</span> Recruiting Portfolio
          </span>
          <ToolBtn testid="portfolio-print" onClick={() => window.print()} icon={Printer}>Print</ToolBtn>
          <ToolBtn testid="portfolio-download-pdf" href={pdfUrl} icon={Download}>PDF</ToolBtn>
          <ToolBtn testid="portfolio-download-card" href={cardUrl} icon={ImageDown} onClick={() => track("share_card_download", p.id, "portfolio")}>Share Card</ToolBtn>
          <ToolBtn testid="portfolio-copy-link" onClick={copy} icon={Copy}>Copy Link</ToolBtn>
          <ToolBtn testid="portfolio-view-profile" to={`/prospects/${p.slug}`} icon={ExternalLink}>Full Profile</ToolBtn>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 sm:p-6 print:p-0 print:max-w-none">
        <div className="bg-white shadow-xl print:shadow-none">
          {/* Header */}
          <div className="relative bg-[#0A192F] text-white p-6 sm:p-8">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#E63946]" />
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <div className="w-28 h-28 sm:w-32 sm:h-32 bg-slate-700 border-2 border-white grid place-items-center overflow-hidden rounded-sm shrink-0">
                {p.profile_photo_url
                  ? <img src={p.profile_photo_url} alt={p.first_name} className="w-full h-full object-cover" />
                  : <span className="font-display text-4xl">{p.first_name?.[0]}{p.last_name?.[0]}</span>}
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="label-overline text-[#E63946]">CBR Prospects · Recruiting Profile</div>
                <h1 data-testid="portfolio-name" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-1 leading-none">
                  {p.first_name} <span className="text-[#E63946]">{p.last_name}</span>
                </h1>
                <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-1 text-sm text-white/80">
                  {p.graduation_year && <span><b className="text-white font-data">Class of {p.graduation_year}</b></span>}
                  {p.primary_position && <span className="font-data">{p.primary_position}{p.secondary_position ? ` / ${p.secondary_position}` : ""}</span>}
                  {p.province && <span>{p.city ? `${p.city}, ` : ""}{p.province}</span>}
                </div>
                <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-1 text-xs text-white/60">
                  {p.school && <span>{p.school}</span>}
                  <span>B/T {p.bats || "—"}/{p.throws || "—"}</span>
                  {p.height_cm && <span>{p.height_cm} cm</span>}
                  {p.weight_kg && <span>{p.weight_kg} kg</span>}
                  {p.gpa && <span>GPA {p.gpa}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Rating + rankings strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 border-b border-slate-200">
            <div className="bg-[#0A192F] text-white p-4 col-span-2 sm:col-span-1" data-testid="portfolio-rating">
              <div className="label-overline text-[#E63946] text-[10px]">CBR Rating</div>
              <div className="font-data text-4xl mt-1">{p.cbr_overall_rating != null ? round1(p.cbr_overall_rating) : "—"}</div>
            </div>
            <RankCell label="National" v={rk.national} t={rk.national_total} />
            <RankCell label={rk.province || "Provincial"} v={rk.provincial} t={rk.provincial_total} />
            <RankCell label={rk.primary_position || "Position"} v={rk.position} t={rk.position_total} />
            <RankCell label={rk.graduation_year ? `Class ${rk.graduation_year}` : "Class"} v={rk.graduation_year_rank} t={rk.graduation_year_total} />
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            {/* Commitment */}
            {d.commitment?.school_name && (
              <div data-testid="portfolio-commitment" className="flex items-center gap-3 bg-[#E63946] text-white p-4 rounded-sm">
                {d.commitment.school_logo_url
                  ? <SchoolLogo name={d.commitment.school_name} logoUrl={d.commitment.school_logo_url} darkLogoUrl={d.commitment.school_logo_dark_url} dark size="lg" />
                  : <GraduationCap className="w-6 h-6 shrink-0" />}
                <div>
                  <div className="label-overline text-white/70 text-[10px]">Committed</div>
                  <div className="font-display text-xl uppercase tracking-tight">{d.commitment.school_name}{d.commitment.level ? ` · ${d.commitment.level}` : ""}</div>
                </div>
              </div>
            )}

            {/* Development summary */}
            {dev.status === "ready" && (dev.metrics?.length || dev.rankings?.length || dev.rating) ? (
              <Section icon={TrendingUp} label="Development" title="Progress Since First CBR Event">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portfolio-development">
                  {dev.metrics?.map((m, i) => <DeltaCell key={`m${i}`} label={m.label} text={`${m.delta > 0 ? "+" : ""}${m.delta}${m.units ? " " + m.units : ""}`} up={m.improved} />)}
                  {dev.rankings?.map((r, i) => <DeltaCell key={`r${i}`} label={r.label} text={`${r.improved ? "↑" : "↓"} ${Math.abs(r.places)} places`} up={r.improved} />)}
                  {dev.rating && <DeltaCell label="CBR Rating" text={`${dev.rating.delta > 0 ? "+" : ""}${dev.rating.delta}`} up={dev.rating.improved} />}
                </div>
              </Section>
            ) : null}

            {/* Top verified metrics */}
            {d.top_metrics?.length > 0 && (
              <Section icon={Activity} label="Performance" title="Top Verified Metrics">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portfolio-metrics">
                  {d.top_metrics.map((m) => (
                    <div key={m.key} className="border border-slate-200 rounded-sm p-4">
                      <div className="flex items-center justify-between">
                        <div className="label-overline text-[10px]">{m.label}</div>
                        {(m.verification_status === "cbr_verified" || m.verification_status === "staff_entered") && (
                          <ShieldCheck className="w-3.5 h-3.5 text-[#E63946]" />
                        )}
                      </div>
                      <div className="font-data text-3xl text-[#0A192F] mt-1">{m.value}{m.units && <span className="text-base ml-1 text-slate-500">{m.units}</span>}</div>
                      {m.event_name && <div className="text-[10px] text-slate-400 mt-1 truncate">{m.event_name}</div>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Featured video */}
            {d.featured_video && (
              <Section icon={PlayCircle} label="Film Room" title="Featured Video">
                <a data-testid="portfolio-video" href={d.featured_video.external_url || d.featured_video.embed_url || d.featured_video.file_url}
                   target="_blank" rel="noreferrer"
                   onClick={() => track("video_view", p.id, "portfolio", { media_id: d.featured_video.id, video_type: d.featured_video.video_type })}
                   className="flex items-center gap-3 border border-slate-200 rounded-sm p-4 hover:border-[#E63946] transition-colors group print:hidden">
                  <div className="w-24 h-16 bg-slate-100 rounded-sm overflow-hidden shrink-0 grid place-items-center">
                    {d.featured_video.thumbnail_url ? <img src={d.featured_video.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <PlayCircle className="w-7 h-7 text-slate-400" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#0A192F] truncate">{d.featured_video.title || "Featured Video"}</div>
                    <div className="text-xs text-slate-500">{d.featured_video.video_type || "Video"} · Tap to watch</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#E63946] ml-auto shrink-0" />
                </a>
                <div className="hidden print:block text-xs text-slate-500">Featured video: {d.featured_video.external_url || d.featured_video.embed_url || "see live profile"}</div>
              </Section>
            )}

            {/* Awards */}
            {d.awards?.length > 0 && (
              <Section icon={Award} label="Recognition" title={`CBR Awards (${d.awards_total})`}>
                <ul className="space-y-2" data-testid="portfolio-awards">
                  {d.awards.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Award className="w-4 h-4 text-[#E63946] shrink-0 mt-0.5" />
                      <span><b>{a.label}</b>{a.stat_value ? ` · ${a.stat_value}${a.stat_units ? " " + a.stat_units : ""} ${a.stat_label || ""}` : ""}{a.camp_title ? <span className="text-slate-400"> — {a.camp_year} {a.camp_title}</span> : null}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Timeline highlights */}
            {d.timeline_highlights?.length > 0 && (
              <Section icon={TrendingUp} label="Career" title="Timeline Highlights">
                <ul className="space-y-2" data-testid="portfolio-timeline">
                  {d.timeline_highlights.map((e, i) => (
                    <li key={i} className="flex items-start gap-3 border-l-2 border-[#E63946] pl-3">
                      <div>
                        <div className="text-sm font-semibold text-[#0A192F]">{e.title}</div>
                        {e.description && <div className="text-xs text-slate-500">{e.description}</div>}
                        {e.date && <div className="text-[10px] text-slate-400 mt-0.5">{new Date(e.date).toLocaleDateString("en-CA", { dateStyle: "medium" })}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Links + QR */}
            <div className="grid sm:grid-cols-3 gap-6 pt-2 border-t border-slate-200">
              <div className="sm:col-span-2">
                <RecruitingLinks player={p} />
              </div>
              <div className="text-center sm:text-right">
                <div className="label-overline text-[#E63946] mb-2">Scan for Live Profile</div>
                <div className="inline-block bg-white p-2 border border-slate-200 rounded-sm" data-testid="portfolio-qr">
                  <QRCodeSVG value={d.profile_url} size={120} fgColor="#0A192F" />
                </div>
                <div className="font-mono text-[10px] text-slate-500 mt-2 break-all">{(d.profile_url || "").replace(/^https?:\/\//, "")}</div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A192F] text-white/50 text-[10px] uppercase tracking-wider px-6 sm:px-8 py-3 text-center">
            CBR Prospects · Canadian Baseball Rankings · Verified Recruiting Data
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolBtn({ children, icon: Icon, onClick, href, to, testid }) {
  const cls = "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/10 hover:bg-white/20 px-3 py-2 rounded-sm transition-colors";
  if (to) return <Link to={to} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</a>;
  return <button onClick={onClick} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</button>;
}

function Section({ icon: Icon, label, title, children }) {
  return (
    <div>
      <div className="label-overline text-[#E63946] flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</div>
      <h2 className="font-display text-2xl uppercase tracking-tight mt-1 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function RankCell({ label, v, t }) {
  return (
    <div className="p-4 border-l border-slate-100">
      <div className="label-overline text-[10px] truncate">{label}</div>
      <div className="font-data text-2xl text-[#0A192F] mt-1">{v ? `#${v}` : "—"}{v && t ? <span className="text-xs text-slate-400 ml-1">/{t}</span> : null}</div>
    </div>
  );
}

function DeltaCell({ label, text, up }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-data text-xl mt-0.5 ${up ? "text-emerald-600" : "text-amber-600"}`}>{text}</div>
    </div>
  );
}

function RecruitingLinks({ player }) {
  const h = (v) => String(v || "").replace(/^@/, "");
  const TARGET = {
    instagram_handle: "instagram", x_handle: "x", tiktok_handle: "tiktok", youtube_handle: "youtube",
    perfect_game_url: "perfect_game", prep_baseball_url: "prep_baseball", ncsa_url: "ncsa",
    fieldlevel_url: "fieldlevel", img_url: "img", highlight_video_url: "highlight_video",
    hudl_url: "hudl", personal_website_url: "personal_website",
  };
  const links = [
    { k: "instagram_handle", label: "Instagram", icon: Instagram, url: player.instagram_handle && `https://instagram.com/${h(player.instagram_handle)}` },
    { k: "x_handle", label: "X / Twitter", icon: Twitter, url: player.x_handle && `https://x.com/${h(player.x_handle)}` },
    { k: "tiktok_handle", label: "TikTok", icon: Music2, url: player.tiktok_handle && `https://tiktok.com/@${h(player.tiktok_handle)}` },
    { k: "youtube_handle", label: "YouTube", icon: Youtube, url: player.youtube_handle && (String(player.youtube_handle).startsWith("http") ? player.youtube_handle : `https://youtube.com/@${h(player.youtube_handle)}`) },
    { k: "perfect_game_url", label: "Perfect Game", icon: ExternalLink, url: player.perfect_game_url },
    { k: "prep_baseball_url", label: "Prep Baseball", icon: ExternalLink, url: player.prep_baseball_url },
    { k: "ncsa_url", label: "NCSA", icon: ExternalLink, url: player.ncsa_url },
    { k: "fieldlevel_url", label: "FieldLevel", icon: ExternalLink, url: player.fieldlevel_url },
    { k: "img_url", label: "IMG Academy", icon: ExternalLink, url: player.img_url },
    { k: "highlight_video_url", label: "Highlight Video", icon: Video, url: player.highlight_video_url },
    { k: "hudl_url", label: "Hudl", icon: Video, url: player.hudl_url },
    { k: "personal_website_url", label: "Website", icon: Globe, url: player.personal_website_url },
  ].filter((l) => l.url);

  if (links.length === 0) return null;
  return (
    <div data-testid="portfolio-links">
      <div className="label-overline text-[#E63946] flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Recruiting & Social</div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {links.map((l) => (
          <a key={l.k} href={l.url} target="_blank" rel="noreferrer" data-testid={`portfolio-link-${l.k}`}
             onClick={() => track("link_click", player.id, "portfolio", { target: TARGET[l.k] || l.k, url: l.url })}
             className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm hover:border-[#0A192F] transition-colors">
            <l.icon className="w-4 h-4 text-[#0A192F] shrink-0" />
            <span className="text-xs font-semibold text-[#0A192F] truncate">{l.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

const round1 = (v) => { const f = Number(v); return f === Math.round(f) ? f : Math.round(f * 10) / 10; };

function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center bg-[#F1F3F6]"><div className="label-overline text-slate-500">{children}</div></div>;
}
